import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { Checkbox, Grid, Toolbar, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { noop, pick, without } from 'lodash';
import SearchField from '../SearchField';
import useChangeEvent from '../../hooks/useChangeEvent';
import useDataGridSearch from '../../hooks/useDataGridSearch';
import useLogic from './logic';
import SelectionContext from './SelectionContext';

const DefaultPageSize = 10;
const DefaultPageSizeOptions = [5, 10, 20, 50, 100];

/** @type {import('@mui/x-data-grid').GridSlotsComponent} */
const OverrideComponents = {
    ColumnResizeIcon: () => null,
    Checkbox: forwardRef((props, ref) => {
        props = pick(props, [
            'checked',
            'className',
            'classes',
            'icon',
            'checkedIcon',
            'color',
            'indeterminate',
            'tabIndex',
        ]);

        return <Checkbox {...props} />;
    }),
};

/**
 * @typedef Props
 * @property {string} [searchField]
 * @property {number} [defaultPageSize]
 * @property {'default' | 'links'} [variant]
 * @property {ReturnType<useStyles>} [classes]
 * @property {(params: import('@mui/x-data-grid').GridRenderCellParams) => string} [getRowUrl]
 * @property {'single' | 'checkboxes' | 'ordered-checkboxes'} [selectionType]
 * @property {() => void} [overrideSelectAll]
 * @property {() => void} [overrideSelectNone]
 * @property {JSX.Element} [toolbarContent]
 * @property {number} [rowCount]
 * @property {Array<object>} rows
 * @property {Array<object>} columns

 */

/**
 * @param {Props & import('@mui/x-data-grid').DataGridProps} props
 */
function DataGridDyl(props) {
    const { children } = props;

    const {
        className: propsClassName,
        classes,
        components: PropsOverrideComponents,
        selectionType = 'single',
        searchField,
        defaultPageSize,
        variant,
        selectionModel,
        onSelectionModelChange,
        selectAll, selectNone,
        disableSelectionOnClick: propsDisableSelectionOnClick,
        toolbarContent,
        rows,
        columns,
        handlePageSizeChange,
        ...passedProps
    } = useLogic(props);

    const [pageSize, setPageSize] = useState(defaultPageSize ?? DefaultPageSize);
    const onPageSizeChange = useCallback(s => setPageSize(s), []);
    const [searchQuery, setSearchQuery] = useState('');
    const onSearch = useChangeEvent(setSearchQuery);
    const filterModel = useDataGridSearch(searchField, searchQuery);

    const [_filteredRowCount, setFilteredRowCount] = useState(null);
    const rowCount = props?.rowCount ?? props?.rows?.length ?? 0;
    const filteredRowCount = _filteredRowCount ?? rowCount;

    const onStateChange = useCallback(
        /** @param {import('@mui/x-data-grid').GridState} state */
        state => {
            const visible = state?.filter?.filteredRowsLookup;
            setFilteredRowCount(
                !!visible && Object.entries(visible).length > 0
                    ? Object.entries(visible).filter(x => x[1]).length
                    : state.rows.totalRowCount);
        }, []
    );

    const selectionContextValue = useMemo(() => ({
        selectionModel,
        onSelectionModelChange,
    }), [selectionModel, onSelectionModelChange]);

    const disableSelectionOnClick = propsDisableSelectionOnClick || variant === 'links';

    return (
        <>
            <Grid container spacing={0}>
                {!!searchField && (
                    <SearchField item md={3}
                        className={classes.search}
                        value={searchQuery}
                        onChange={onSearch}
                    />
                )}
                {children}
            </Grid>

            <Grid container>
                <Grid item xs={12} md={4}>
                    {!!searchField && (
                        <Typography variant='h6' className={classes.xOfYText}>
                            Showing {filteredRowCount} of {rowCount} records.
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} md={8}>
                    {!!toolbarContent && (
                        <Toolbar className={classes.toolbar} variant='dense'>
                            {toolbarContent}
                        </Toolbar>
                    )}
                </Grid>
            </Grid>

            <SelectionContext.Provider value={selectionContextValue}>
                <DataGrid
                    className={cn(
                        variant === 'links' && classes.links,
                        selectionType === 'ordered-checkboxes' && classes.orderedCheckboxes,
                        propsClassName,
                    )}
                    autoHeight={true}
                    sx={{ height: "628px", overflow: "auto" }}
                    rows={rows}
                    columns={columns}
                    pagination={true}
                    pageSizeOptions={DefaultPageSizeOptions}
                    hideFooterPagination={false}
                    hideFooterSelectedRowCount={false}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                    rowsPerPageOptions={DefaultPageSizeOptions}
                    filterModel={filterModel}
                    disableColumnMenu={!!searchField}
                    checkboxSelection={selectionType !== 'single'}
                    components={{ ...OverrideComponents, ...PropsOverrideComponents }}
                    onStateChange={onStateChange}
                    {...passedProps}
                    classes={classes}
                    selectionModel={selectionModel}

                    // Custom selection handling, allowing for overrides, i.e. for selection working with server-pagination
                    disableSelectionOnClick={disableSelectionOnClick}
                    onSelectionModelChange={onSelectionModelChange ?? noop}
                    onRowClick={p => {
                        if (disableSelectionOnClick) {
                            return;
                        }

                        if (selectionModel.includes(p.id)) {
                            onSelectionModelChange(without(selectionModel, p.id));
                        }
                        else {
                            onSelectionModelChange([...selectionModel, p.id]);
                        }
                    }}
                    onColumnHeaderClick={p => {
                        if (p.field === '__check__') {
                            if (selectionModel.length > 0) {
                                selectNone();
                            }
                            else {
                                selectAll();
                            }
                        }
                    }}
                />
            </SelectionContext.Provider>
        </>
    );
}

DataGridDyl.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowCount: PropTypes.number,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default DataGridDyl;